import { Link } from "react-router-dom";
import type { AnnouncementDelivery } from "../../../hooks/useAdminAnnouncements";
import { DELIVERY_LABEL, describeWhere, PREVIEW_LABEL } from "../../../lib/announcements";
import { formatDateTime } from "../../../utils/formatDateTime";
import shared from "../../../pages/AdminInstances.module.css";
import own from "./Announcements.module.css";

export const DELIVERY_HEADERS = ["Instance", "Would land", "Status", "Channel", "Detail", "Finished"];

type Props = { deliveries: ReadonlyArray<AnnouncementDelivery>; isFetching: boolean };

export const DeliveryTable = ({ deliveries, isFetching }: Props): JSX.Element => (
  <table
    className={`${shared.table} ${own.compactTable}`}
    data-testid="announcement-deliveries"
    data-fetching={isFetching ? "true" : "false"}
  >
    <thead>
      <tr>
        {DELIVERY_HEADERS.map((header) => (
          <th key={header}>{header}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {deliveries.map((row) => (
        <DeliveryRow key={row.tenantId} row={row} />
      ))}
    </tbody>
  </table>
);

const DeliveryRow = ({ row }: { row: AnnouncementDelivery }): JSX.Element => {
  const preview = PREVIEW_LABEL[row.previewStatus];
  const status = DELIVERY_LABEL[row.status];
  return (
    <tr data-testid="announcement-delivery-row" data-tenant={row.tenantId}>
      <td>
        <Link className={own.link} to={`/dashboard/admin/instances/${encodeURIComponent(row.tenantId)}`}>
          {row.tenantName}
        </Link>
        <div className={own.secondary}>{row.ownerEmail}</div>
      </td>
      <td>
        <span className={own[preview.tone]}>{preview.label}</span>
        <div className={own.secondary}>
          {row.previewStatus === "would_deliver"
            ? describeWhere(row.previewChannel, row.previewTarget, row.previewSource)
            : row.previewReason}
        </div>
      </td>
      <td data-testid={`announcement-status-${row.tenantId}`}>
        <span className={own[status.tone]}>{status.label}</span>
        {row.attempts > 1 && <div className={own.secondary}>{row.attempts} attempts</div>}
      </td>
      <td>{describeWhere(row.channel, row.target, row.source)}</td>
      <td>
        <DeliveryDetail row={row} />
      </td>
      <td className={`${own.secondary} ${shared.nowrap}`}>
        {row.finishedAt === null ? "-" : formatDateTime(row.finishedAt)}
      </td>
    </tr>
  );
};

const DeliveryDetail = ({ row }: { row: AnnouncementDelivery }): JSX.Element | null => {
  if (row.status !== "delivered") return row.reason === null ? null : <div className={own.reason}>{row.reason}</div>;
  const aware = row.agentAware === false ? "Agent not told: the prompt hook is not permitted on this box" : "Agent knows about it";
  const slow = row.timedOut ? ". The send timed out on the box but almost certainly went out" : "";
  return <div className={own.secondary}>{`${aware}${slow}`}</div>;
};
